<template>
  <div class="impersonation-request">
    <div class="page-head">
      <div class="page-ico"><i class="ik ik-shield" /></div>
      <div>
        <h1>Solicitar personificação</h1>
        <p>Permita que o suporte acesse sua conta com segurança</p>
      </div>
    </div>

    <ErrorAlert :error="error" :centered="true" />

    <div class="d-flex justify-content-center">
      <div class="card impersonation-card">
        <div class="card-body text-center">
          <div class="shield-badge"><i class="ik ik-shield" /></div>

          <template v-if="loading">
            <p class="text-muted mb-0">Carregando...</p>
          </template>

          <template v-else-if="generatedCode !== null">
            <h2>Código de acesso gerado</h2>
            <p class="description">Informe este código ao agente de suporte da <b>API4COM</b> para liberar o acesso à sua conta. Compartilhe apenas com agentes oficiais.</p>
            <div class="code-box">
              <span class="code">{{ generatedCode }}</span>
            </div>
            <button
              class="btn btn-primary btn-block"
              @click="copyCode">
              <i class="ik ik-copy" /> {{ copyLabel }}
            </button>
            <p class="expires"><i class="ik ik-clock" /> Expira em <b>{{ formatDate(activeRequest.expiratedAt) }}</b></p>
            <div class="card-footer-action">
              <button
                class="btn btn-outline-danger btn-block"
                :disabled="submitting"
                @click="endAccess">Encerrar acesso agora</button>
            </div>
          </template>

          <template v-else-if="activeRequest !== null">
            <h2>Você já tem um código ativo</h2>
            <p class="description">Por segurança, o código só é exibido no momento em que é gerado. Se precisar informá-lo de novo ao suporte, gere um novo código. Se a personificação não é mais necessária, encerre o acesso.</p>
            <p class="expires"><i class="ik ik-clock" /> Expira em <b>{{ formatDate(activeRequest.expiratedAt) }}</b></p>
            <p
              v-if="activeRequest.claimed"
              class="claimed"><i class="ik ik-user-check" /> O suporte já está acessando sua conta.</p>
            <button
              class="btn btn-primary btn-block"
              :disabled="submitting"
              @click="requestCode">Gerar novo código</button>
            <div class="card-footer-action">
              <button
                class="btn btn-outline-danger btn-block"
                :disabled="submitting"
                @click="endAccess">Encerrar acesso agora</button>
            </div>
          </template>

          <template v-else>
            <h2>Solicitar personificação</h2>
            <p class="description">A personificação permite que um agente de suporte da <b>API4COM</b> entre temporariamente na sua conta para investigar e resolver problemas que você esteja enfrentando no portal — <b>sem precisar pedir sua senha</b>. Ao solicitar, geramos um código único válido por 3 horas pra você passar ao agente; a sessão é registrada para auditoria e você pode encerrar o acesso a qualquer momento.</p>
            <button
              class="btn btn-primary btn-block"
              :disabled="submitting"
              @click="requestCode">Solicitar personificação</button>
          </template>
        </div>
      </div>
    </div>

    <impersonation-confirm-modal
      :open="confirmingEnd"
      title="Encerrar acesso agora?"
      cancel-label="Manter ativo"
      confirm-label="Encerrar acesso"
      loading-label="Encerrando..."
      :loading="submitting"
      @cancel="cancelEndAccess"
      @confirm="runEndAccess">
      O suporte perderá o acesso à sua conta imediatamente — o código gerado é invalidado e qualquer agente que esteja dentro é desconectado.
    </impersonation-confirm-modal>
  </div>
</template>

<script>
import loopback from '../../services/loopback'
import ImpersonationConfirmModal from './ImpersonationConfirmModal.vue'

export default {
  name: 'ImpersonationRequest',
  components: { ImpersonationConfirmModal },
  data: () => ({
    loading: true,
    submitting: false,
    confirmingEnd: false,
    error: null,
    generatedCode: null,
    activeRequest: null,
    copyLabel: 'Copiar código'
  }),
  mounted () {
    this.loadActive()
  },
  methods: {
    loadActive () {
      loopback
        .get('/account-impersonations/active')
        .then(result => {
          if (result.active === true) {
            this.activeRequest = {
              id: result.id,
              expiratedAt: result.expiratedAt,
              claimed: result.claimed
            }
          } else {
            this.activeRequest = null
          }
        })
        .catch(error => {
          this.error = error
        })
        .finally(() => {
          this.loading = false
        })
    },
    requestCode () {
      this.error = null
      this.submitting = true
      loopback
        .post('/account-impersonations/request')
        .then(result => {
          this.generatedCode = result.code
          this.activeRequest = {
            id: result.id,
            expiratedAt: result.expiratedAt,
            claimed: false
          }
        })
        .catch(error => {
          this.error = error
        })
        .finally(() => {
          this.submitting = false
        })
    },
    endAccess () {
      this.confirmingEnd = true
    },
    cancelEndAccess () {
      if (this.submitting === true) return
      this.confirmingEnd = false
    },
    runEndAccess () {
      this.error = null
      this.submitting = true
      loopback
        .post(`/account-impersonations/${this.activeRequest.id}/end`)
        .then(() => {
          this.generatedCode = null
          this.activeRequest = null
        })
        .catch(error => {
          this.error = error
        })
        .finally(() => {
          this.submitting = false
          this.confirmingEnd = false
        })
    },
    copyCode () {
      navigator.clipboard.writeText(this.generatedCode).then(() => {
        this.copyLabel = 'Código copiado'
      })
    },
    formatDate (date) {
      return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$purple: #6d28d9;
$green: #16a34a;
$green-soft: #e9f8ef;

.page-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}
.page-ico {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: #233244;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
}
.page-head h1 {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
}
.page-head p {
  margin: 2px 0 0;
  font-size: 13.5px;
  color: #8a93a2;
}

.impersonation-card {
  width: 560px;
  max-width: 100%;
  padding: 16px;
}

.shield-badge {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  margin: 0 auto 14px;
  background: $green-soft;
  color: $green;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

h2 {
  font-size: 20px;
  margin-bottom: 6px;
}

.description {
  color: #475467;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 22px;
}

.code-box {
  background: #f7f8fb;
  border: 1px dashed #cfd6e0;
  border-radius: 12px;
  padding: 22px 0;
  margin-bottom: 16px;
}
.code {
  font-family: "SF Mono", "JetBrains Mono", ui-monospace, monospace;
  letter-spacing: 0.12em;
  font-weight: 700;
  font-size: 38px;
  color: #1f2a37;
}

.expires {
  color: #475467;
  font-size: 13px;
  margin: 18px 0 0;
}
.claimed {
  color: $purple;
  font-size: 13px;
  font-weight: 600;
  margin: 10px 0 0;
}

.card-footer-action {
  border-top: 1px solid #e6e8ed;
  margin-top: 22px;
  padding-top: 18px;
}
</style>



// WEBPACK FOOTER //
// src/components/Support/ImpersonationRequest.vue